import { Fragment, useState } from "react"
import styled from "styled-components"

import { PlusIcon } from "@minuthq/meatball-ui-icons"
import { IconButton } from "@minuthq/meatball-ui-react"

import { EmptyState } from "src/components/EmptyState"
import { ParadiseActionDivider } from "src/components/Paradise/ParadiseDevices/ParadiseDeviceReadings/ParadiseGraphViews/ParadiseActionDivider"
import { ParadiseGraphViewGraph } from "src/components/Paradise/ParadiseDevices/ParadiseDeviceReadings/ParadiseGraphViews/ParadiseGraphViewGraph"
import { ParadiseSelectDeviceReadingTypeDialog } from "src/components/Paradise/ParadiseDevices/ParadiseDeviceReadings/ParadiseGraphViews/ParadiseSelectDeviceReadingTypeDialog"
import { TParadiseGraphView } from "src/components/Paradise/ParadiseDevices/ParadiseDeviceReadings/ParadiseGraphViews/useParadiseGraphViews"
import { DeviceReadingType } from "src/data/devices/types/deviceTypes"
import { TParadiseDevice } from "src/data/paradise/paradiseDevices/types/paradiseDeviceQueryTypes"
import { MButton } from "src/ui/Button/MButton"
import GraphIcon from "src/ui/icons/graph-plot.svg"
import { spacing } from "src/ui/spacing"

export function ParadiseGraphView({
  device,
  graphView,
  dateRange,
}: {
  device: TParadiseDevice
  graphView: TParadiseGraphView
  dateRange: {
    startAt: Date
    endAt: Date
  }
}) {
  const [addGraphDialog, setAddGraphDialog] = useState<{
    rowIndex: number
    columnIndex: number
  } | null>(null)

  const [addRowDialog, setAddRowDialog] = useState<{
    rowIndex: number
  } | null>(null)

  const isEmpty = graphView.graphs.length === 0

  if (isEmpty) {
    return (
      <ParadiseGraphViewLayout
        graphView={graphView}
        device={device}
        addDialog={addGraphDialog}
        onAddDialogClose={() => setAddGraphDialog(null)}
        addRowDialog={addRowDialog}
        onAddRowDialogClose={() => setAddRowDialog(null)}
      >
        <EmptyState
          icon={
            <div>
              <GraphIcon width={86} />
            </div>
          }
          title="No graphs"
        >
          <MButton
            variant="primary"
            size="small"
            onClick={() =>
              setAddGraphDialog({
                rowIndex: 0,
                columnIndex: 0,
              })
            }
          >
            Add first graph
          </MButton>
        </EmptyState>
      </ParadiseGraphViewLayout>
    )
  }

  return (
    <ParadiseGraphViewLayout
      graphView={graphView}
      device={device}
      addDialog={addGraphDialog}
      onAddDialogClose={() => setAddGraphDialog(null)}
      addRowDialog={addRowDialog}
      onAddRowDialogClose={() => setAddRowDialog(null)}
    >
      <Wrapper>
        {graphView.graphs.map((row, rowIndex) => (
          <Fragment key={row.id}>
            {/* Add action divider before first row */}
            {rowIndex === 0 && (
              <ParadiseActionDivider
                horizontal
                actionContent={
                  <IconButton
                    Icon={PlusIcon}
                    variant="minimal"
                    size="small"
                    onClick={() => {
                      setAddRowDialog({ rowIndex })
                    }}
                  />
                }
              />
            )}

            <ParadiseGraphViewRow
              device={device}
              graphViewRow={row}
              dateRange={dateRange}
              onReadingTypeChange={(readingType, columnIndex) => {
                graphView.changeGraphReadingType({
                  rowIndex,
                  columnIndex,
                  readingType,
                })
              }}
              onAddColumn={(columnIndex) => {
                setAddGraphDialog({ rowIndex, columnIndex })
              }}
              onDeleteColumn={(columnIndex) => {
                graphView.removeColumn(rowIndex, columnIndex)
              }}
            />

            {/* Make sure each column has an action divider at the bottom */}
            <ParadiseActionDivider
              horizontal
              actionContent={
                <IconButton
                  Icon={PlusIcon}
                  variant="minimal"
                  size="small"
                  onClick={() => {
                    setAddRowDialog({ rowIndex: rowIndex + 1 })
                  }}
                />
              }
            />
          </Fragment>
        ))}
      </Wrapper>
    </ParadiseGraphViewLayout>
  )
}

function ParadiseGraphViewLayout({
  graphView,
  addDialog,
  onAddDialogClose,
  addRowDialog,
  onAddRowDialogClose,
  device,
  children,
}: {
  graphView: TParadiseGraphView
  addDialog: {
    rowIndex: number
    columnIndex: number
  } | null
  onAddDialogClose: () => void
  addRowDialog: {
    rowIndex: number
  } | null
  onAddRowDialogClose: () => void
  device: TParadiseDevice
  children: React.ReactNode
}) {
  return (
    <div>
      {children}
      <ParadiseSelectDeviceReadingTypeDialog
        title="Add graph"
        open={!!addDialog}
        onClose={onAddDialogClose}
        device={device}
        onSubmit={(readingType) => {
          if (addDialog) {
            graphView.addColumn(
              addDialog.rowIndex,
              addDialog.columnIndex,
              readingType
            )
          }
        }}
      />

      <ParadiseSelectDeviceReadingTypeDialog
        title="Add row"
        open={!!addRowDialog}
        onClose={onAddRowDialogClose}
        device={device}
        onSubmit={(readingType) => {
          if (addRowDialog) {
            graphView.addRow(addRowDialog.rowIndex, readingType)
          }
        }}
      />
    </div>
  )
}

function ParadiseGraphViewRow({
  graphViewRow,
  device,
  dateRange,
  onReadingTypeChange,
  onAddColumn,
  onDeleteColumn,
}: {
  graphViewRow: TParadiseGraphView["graphs"][number]
  device: TParadiseDevice
  dateRange: {
    startAt: Date
    endAt: Date
  }
  onReadingTypeChange: (
    readingType: DeviceReadingType,
    columnIndex: number
  ) => void
  onAddColumn: (columnIndex: number) => void
  onDeleteColumn: (columnIndex: number) => void
}) {
  return (
    <Row>
      {graphViewRow.columns.map((column, columnIndex) => {
        return (
          <Fragment key={column.id}>
            {/* Add action divider before first column */}
            {columnIndex === 0 && (
              <ParadiseActionDivider
                actionContent={
                  <IconButton
                    Icon={PlusIcon}
                    variant="minimal"
                    size="small"
                    onClick={() => onAddColumn(columnIndex)}
                  />
                }
                disabled={graphViewRow.isFull}
              />
            )}

            <ParadiseGraphViewGraph
              device={device}
              deviceReadingType={column.readingType}
              onReadingTypeChange={(readingType) => {
                onReadingTypeChange(readingType, columnIndex)
              }}
              onGraphDelete={() => onDeleteColumn(columnIndex)}
              dateRange={dateRange}
            />

            {/* Make sure each column has an action divider to the right */}
            <ParadiseActionDivider
              actionContent={
                <IconButton
                  Icon={PlusIcon}
                  variant="minimal"
                  size="small"
                  onClick={() => {
                    // The button is rendered after the column
                    // Thus the new column should be added after the current column
                    onAddColumn(columnIndex + 1)
                  }}
                />
              }
              disabled={graphViewRow.isFull}
            />
          </Fragment>
        )
      })}
    </Row>
  )
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.XS3};
`

const Row = styled.div`
  display: flex;
  gap: ${spacing.XS3};
`
